// src/pages/Dashboard/Admin/Inventaire/components/EditInventaireDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaImage, FaSave, FaTimes, FaTrash, FaUpload } from "react-icons/fa";

import {
  DEFAULT_INVENTAIRE_PHOTO,
  getInventairePhotoSrc,
  toStockNumber,
  withCacheBust,
} from "../utils/inventaireDashboardUtils";

const initialForm = {
  categorie: "",
  nom: "",
  stock: "",
  note: "",
  photo_url: "",
};

export default function EditInventaireDrawer({
  isOpen,
  onClose,
  inventaire,
  onSubmit,
  onDelete,
  onUploadPhoto,
  saving,
  deletingId,
}) {
  const [formData, setFormData] = useState(initialForm);
  const [imgError, setImgError] = useState(false);
  const [imgVersion, setImgVersion] = useState(0);

  const [selectedFile, setSelectedFile] = useState(null);
  const [outputFormat, setOutputFormat] = useState("webp");
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (!isOpen || !inventaire) return;

    setFormData({
      categorie: inventaire.categorie || "",
      nom: inventaire.nom || "",
      stock:
        inventaire.stock === null || inventaire.stock === undefined
          ? ""
          : String(inventaire.stock),
      note: inventaire.note || "",
      photo_url: inventaire.photo_url || "",
    });

    setImgError(false);
    setSelectedFile(null);
    setUploadProgress(0);
    setOutputFormat("webp");
    setImgVersion((prev) => prev + 1);
  }, [isOpen, inventaire]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [isOpen, saving, onClose]);

  const canSubmit = useMemo(() => {
    return (
      inventaire?.id &&
      String(formData.categorie || "").trim() &&
      String(formData.nom || "").trim() &&
      String(formData.stock || "").trim() !== "" &&
      toStockNumber(formData.stock) >= 0 &&
      !saving
    );
  }, [inventaire?.id, formData, saving]);

  const isDirty = useMemo(() => {
    if (!inventaire) return false;

    return (
      String(formData.categorie || "") !== String(inventaire.categorie || "") ||
      String(formData.nom || "") !== String(inventaire.nom || "") ||
      String(formData.stock || "") !== String(inventaire.stock ?? "") ||
      String(formData.note || "") !== String(inventaire.note || "") ||
      String(formData.photo_url || "") !== String(inventaire.photo_url || "")
    );
  }, [formData, inventaire]);

  const previewSrc = useMemo(() => {
    if (imgError) return DEFAULT_INVENTAIRE_PHOTO;

    return withCacheBust(getInventairePhotoSrc(formData.photo_url), imgVersion);
  }, [formData.photo_url, imgError, imgVersion]);

  const updateField = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: name === "stock" ? value.replace(",", ".") : value,
    }));

    if (name === "photo_url") {
      setImgError(false);
      setImgVersion((prev) => prev + 1);
    }
  };

  const handleClose = () => {
    if (saving) return;

    if (!isDirty || window.confirm("Des modifications ne sont pas enregistrées. Fermer ?")) {
      onClose?.();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    await onSubmit?.(inventaire.id, formData);
  };

  const handleUpload = async () => {
    if (!selectedFile || !inventaire?.id) return;

    const validType = /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type);

    if (!validType) {
      alert("Format d’image non supporté. Utilisez PNG, JPG ou WEBP.");
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      alert("Fichier trop volumineux. Maximum 10 Mo.");
      return;
    }

    setUploadProgress(0);

    const photoUrl = await onUploadPhoto?.({
      inventaireId: inventaire.id,
      file: selectedFile,
      format: outputFormat,
      onUploadProgress: (event) => {
        if (!event.total) return;
        setUploadProgress(Math.round((event.loaded * 100) / event.total));
      },
    });

    if (photoUrl) {
      setFormData((prev) => ({
        ...prev,
        photo_url: photoUrl,
      }));

      setImgError(false);
      setImgVersion((prev) => prev + 1);
      setSelectedFile(null);
      setUploadProgress(0);
    }
  };

  if (!isOpen || !inventaire) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Modification
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Modifier l’objet
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Modifiez les informations de stock ou téléversez une nouvelle
              image optimisée.
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto p-5"
        >
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[260px,1fr]">
            <div className="space-y-4">
              <div className="relative flex h-52 items-center justify-center overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60">
                <img
                  src={previewSrc}
                  alt={formData.nom || "Objet inventaire"}
                  onError={() => setImgError(true)}
                  className="h-full w-full object-contain"
                />

                <span className="absolute bottom-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm dark:bg-white dark:text-slate-900">
                  <FaImage />
                </span>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Téléverser une image
                </p>

                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) =>
                    setSelectedFile(event.target.files?.[0] || null)
                  }
                  className="mt-3 block w-full text-sm text-slate-700 dark:text-slate-200"
                />

                <div className="mt-3 flex items-center gap-2">
                  <span className="text-sm text-slate-600 dark:text-slate-300">
                    Format
                  </span>

                  <select
                    value={outputFormat}
                    onChange={(event) => setOutputFormat(event.target.value)}
                    disabled={saving}
                    className="rounded-xl border border-slate-200 bg-white px-2 py-1 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  >
                    <option value="webp">WEBP</option>
                    <option value="jpg">JPG</option>
                    <option value="png">PNG</option>
                  </select>
                </div>

                {uploadProgress > 0 ? (
                  <div className="mt-3">
                    <progress
                      className="h-2 w-full overflow-hidden rounded-full"
                      value={uploadProgress}
                      max="100"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      {uploadProgress}%
                    </p>
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={!selectedFile || saving}
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaUpload />
                  Téléverser
                </button>

                <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  L’image sera optimisée côté serveur. Vous pouvez aussi coller
                  une URL dans le champ photo.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Catégorie
                </label>

                <input
                  type="text"
                  value={formData.categorie}
                  onChange={(event) =>
                    updateField("categorie", event.target.value)
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Nom
                </label>

                <input
                  type="text"
                  value={formData.nom}
                  onChange={(event) => updateField("nom", event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  required
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Stock
                  </label>

                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.stock}
                    onChange={(event) =>
                      updateField("stock", event.target.value)
                    }
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Photo URL
                  </label>

                  <input
                    type="text"
                    value={formData.photo_url}
                    onChange={(event) =>
                      updateField("photo_url", event.target.value)
                    }
                    placeholder="https://... ou /uploads/..."
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Note
                </label>

                <textarea
                  value={formData.note}
                  onChange={(event) => updateField("note", event.target.value)}
                  rows={6}
                  placeholder="État, taille, localisation, informations utiles..."
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />
              </div>
            </div>
          </div>
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={() => onDelete?.(inventaire)}
              disabled={deletingId === inventaire.id || saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-bold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
            >
              <FaTrash />
              Supprimer
            </button>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}